import { api } from "../lib/api";

export type LoanType = "bank_loan" | "directors_loan_in" | "other";
export type LoanStatus = "active" | "settled";

export interface LoanSummary {
  id: string;
  company_id: string;
  lender_name: string;
  reference: string | null;
  loan_type: LoanType;
  start_date: string | null;
  original_principal: string | null;
  annual_interest_rate: string | null;
  term_months: number | null;
  opening_balance: string;
  opening_balance_date: string;
  status: LoanStatus;
  notes: string | null;
  outstanding_balance: string;
  settlement_date: string | null;
  settlement_adjustment: string | null;
  settlement_note: string | null;
  created_at: string;
  updated_at: string;
}

/** Loan option labels everywhere a loan is picked (transaction rows, the split
 * dialog, category rules) — leads with the user's own identifier when set, so loans
 * stay distinguishable at a glance rather than by lender name alone (multiple loans
 * with the same lender aren't unusual). */
export function loanOptionLabel(loan: LoanSummary): string {
  return loan.reference ? `${loan.reference} ${loan.lender_name}` : loan.lender_name;
}

export interface LoanHistoryItem {
  transaction_id: string;
  split_id: string | null;
  date: string;
  description: string;
  amount: string;
  tax_group: string;
}

export interface Loan extends LoanSummary {
  history: LoanHistoryItem[];
}

export interface LoanInput {
  lender_name: string;
  reference?: string | null;
  loan_type: LoanType;
  start_date?: string | null;
  original_principal?: string | null;
  annual_interest_rate?: string | null;
  term_months?: number | null;
  opening_balance: string;
  opening_balance_date: string;
  status?: LoanStatus;
  notes?: string | null;
}

export interface SplitSuggestion {
  interest: string;
  principal: string;
  /** "schedule" = the lender's own figures for a matched instalment; "estimated" = the
   * app's amortisation guess. The dialog says which — they aren't equally trustworthy. */
  source: "schedule" | "estimated";
  instalment_number: number | null;
  due_date: string | null;
  adjusted: boolean;
  fallback_reason: string | null;
}

export interface LoanScheduleEntry {
  id: string;
  instalment_number: number;
  due_date: string;
  interest_amount: string;
  principal_amount: string;
  total_amount: string;
  lender_status: string | null;
  settled_per_lender: boolean;
  matched_transaction_id: string | null;
  superseded: boolean;
  balance_after: string;
}

export interface ScheduleSummary {
  instalment_count: number;
  matched_count: number;
  settled_per_lender_count: number;
  superseded_count: number;
  scheduled_principal_remaining: string;
  outstanding_balance: string;
}

export interface LoanSchedule {
  loan_id: string;
  entries: LoanScheduleEntry[];
  summary: ScheduleSummary;
}

export interface SchedulePreviewRow {
  row_number: number;
  due_date: string | null;
  principal: string | null;
  interest: string | null;
  total: string | null;
  lender_status: string | null;
  settled_per_lender: boolean;
  errors: string[];
}

export interface SchedulePreview {
  header: string[];
  rows: SchedulePreviewRow[];
  valid_count: number;
  error_count: number;
  settled_count: number;
  date_order_ambiguous: boolean;
  skipped_rows: string[];
}

export interface ScheduleImportResult extends LoanSchedule {
  created_count: number;
  matched_count: number;
  auto_split_count: number;
  settled_per_lender_count: number;
  date_order_ambiguous: boolean;
  skipped_rows: string[];
}

export interface SettlementInput {
  transaction_id: string;
  interest_amount?: string;
  settlement_adjustment?: string;
  settlement_note?: string | null;
}

export interface SettlementOutcome {
  loan_id: string;
  transaction_id: string;
  interest_amount: string;
  principal_amount: string;
  balance_before: string;
  residue: string;
  superseded_count: number;
}

export interface SettlementResult {
  loan: LoanSummary;
  outcome: SettlementOutcome;
}

export interface InterestReconciliationCandidate {
  transaction_id: string;
  date: string;
  description: string;
  amount: string;
  current_interest_amount: string;
}

export interface InterestReconciliationOptions {
  app_recorded_interest: string;
  candidates: InterestReconciliationCandidate[];
}

export interface InterestReconciliationInput {
  start_date: string;
  end_date: string;
  statement_interest: string;
  transaction_id: string;
}

export function listLoans(): Promise<LoanSummary[]> {
  return api.get<LoanSummary[]>("/api/loans");
}

export function getLoan(id: string): Promise<Loan> {
  return api.get<Loan>(`/api/loans/${id}`);
}

export function createLoan(data: LoanInput): Promise<LoanSummary> {
  return api.post<LoanSummary>("/api/loans", data);
}

export function updateLoan(
  id: string,
  data: LoanInput & { updated_at: string },
): Promise<LoanSummary> {
  return api.put<LoanSummary>(`/api/loans/${id}`, data);
}

export function deleteLoan(id: string): Promise<void> {
  return api.delete(`/api/loans/${id}`);
}

export function getSplitSuggestion(loanId: string, txnId: string): Promise<SplitSuggestion> {
  return api.get<SplitSuggestion>(
    `/api/loans/${loanId}/split-suggestion?txn=${encodeURIComponent(txnId)}`,
  );
}

export function getInterestReconciliationOptions(
  loanId: string,
  startDate: string,
  endDate: string,
): Promise<InterestReconciliationOptions> {
  const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
  return api.get<InterestReconciliationOptions>(
    `/api/loans/${loanId}/interest-reconciliation?${params.toString()}`,
  );
}

export function reconcileInterest(
  loanId: string,
  data: InterestReconciliationInput,
): Promise<LoanSummary> {
  return api.post<LoanSummary>(`/api/loans/${loanId}/interest-reconciliation`, data);
}

export function previewSchedule(loanId: string, file: File): Promise<SchedulePreview> {
  return api.upload<SchedulePreview>(`/api/loans/${loanId}/schedule/preview`, file);
}

export function replaceSchedule(loanId: string, file: File): Promise<ScheduleImportResult> {
  return api.upload<ScheduleImportResult>(`/api/loans/${loanId}/schedule`, file, "PUT");
}

export function getSchedule(loanId: string): Promise<LoanSchedule> {
  return api.get<LoanSchedule>(`/api/loans/${loanId}/schedule`);
}

export function deleteSchedule(loanId: string): Promise<void> {
  return api.delete(`/api/loans/${loanId}/schedule`);
}

export function previewSettlement(
  loanId: string,
  data: SettlementInput,
): Promise<SettlementOutcome> {
  return api.post<SettlementOutcome>(`/api/loans/${loanId}/settlement/preview`, data);
}

export function settleLoan(loanId: string, data: SettlementInput): Promise<SettlementResult> {
  return api.post<SettlementResult>(`/api/loans/${loanId}/settlement`, data);
}
